import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { HtsChatResultModel } from '@data-layer/models/common';

/**
 * The single suggested HTS code: the shared code card (`app-hts-code-card`, #13533 — digit boxes and the tariff description
 * carried by the result itself, #13513) with the chat's Save-to-catalog / Insert / Start over actions and notes (#13383).
 * The classifier's validator note is not shown (product decision 2026-10-15). Confidence and alternatives are intentionally
 * not rendered. A stale result (superseded by a restart) can neither be inserted nor saved: only the latest suggestion is
 * actionable.
 */
@Component({
  selector: 'app-hts-assistant-result-card',
  templateUrl: './hts-assistant-result-card.component.html',
  styleUrls: ['./hts-assistant-result-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HtsAssistantResultCardComponent {
  @Input() public result: HtsChatResultModel;
  @Input() public isStale: boolean = false;
  @Input() public isReadonly: boolean = false;
  /** Host-level hold (a turn or restart is running). */
  @Input() public isDisabled: boolean = false;
  @Input() public isInserted: boolean = false;
  @Input() public isSaved: boolean = false;
  /** Already in the company's Bina Catalog: the save action becomes a static indicator. */
  @Input() public isInCatalog: boolean = false;
  @Input() public isInserting: boolean = false;
  @Input() public isSaving: boolean = false;
  /** Do-over history: result of a superseded conversation — gray. */
  @Input() public isDimmed: boolean = false;
  /** Result of the active conversation: Start over is enabled here only (a stale card keeps a disabled one). */
  @Input() public isStartOverAvailable: boolean = false;
  @Output() public insert: EventEmitter<void> = new EventEmitter<void>();
  @Output() public saveToCatalog: EventEmitter<void> = new EventEmitter<void>();
  @Output() public startOver: EventEmitter<void> = new EventEmitter<void>();

  public get isShowActions(): boolean {
    return !this.isReadonly;
  }

  public get isBusy(): boolean {
    return this.isInserting || this.isSaving || this.isDisabled;
  }

  public get isInsertDisabled(): boolean {
    return this.isStale || this.isInserted || this.isBusy;
  }

  public get isCatalogDone(): boolean {
    return this.isSaved || this.isInCatalog;
  }

  public get isSaveDisabled(): boolean {
    return this.isStale || this.isCatalogDone || this.isBusy;
  }

  public get isStartOverDisabled(): boolean {
    return !this.isStartOverAvailable || this.isBusy;
  }

  public get startOverTooltipKey(): string {
    return this.isStale ? 'HTS_ASSISTANT.RESULT.SUPERSEDED_SHORT' : 'HTS_ASSISTANT.START_OVER';
  }

  public get insertTooltipKey(): string {
    if (this.isInserted) {
      return 'HTS_ASSISTANT.RESULT.INSERTED';
    }
    return this.isStale ? 'HTS_ASSISTANT.RESULT.SUPERSEDED_SHORT' : 'HTS_ASSISTANT.RESULT.INSERT';
  }

  public get saveTooltipKey(): string {
    if (this.isStale && !this.isCatalogDone) {
      return 'HTS_ASSISTANT.RESULT.SUPERSEDED_SHORT';
    }
    if (this.isInCatalog && !this.isSaved) {
      return 'HTS_ASSISTANT.RESULT.ALREADY_IN_CATALOG';
    }
    return this.isSaved ? 'HTS_ASSISTANT.RESULT.SAVED_TO_CATALOG' : 'HTS_ASSISTANT.RESULT.SAVE_TO_CATALOG';
  }

  public get isShowDoneNote(): boolean {
    return this.isInserted || this.isCatalogDone;
  }

  public get isShowBothDone(): boolean {
    return this.isInserted && this.isCatalogDone;
  }

  public get catalogNoteKey(): string {
    return this.isInCatalog && !this.isSaved ? 'HTS_ASSISTANT.RESULT.ALREADY_IN_CATALOG' : 'HTS_ASSISTANT.RESULT.SAVED_TO_CATALOG';
  }

  /** "No longer available" only makes sense for a result that was never used. */
  public get isShowStaleNote(): boolean {
    return this.isStale && !this.isInserted && !this.isCatalogDone && !this.isReadonly;
  }

  public onInsert(): void {
    if (!this.isInsertDisabled) {
      this.insert.emit();
    }
  }

  public onSave(): void {
    if (!this.isSaveDisabled) {
      this.saveToCatalog.emit();
    }
  }

  public onStartOver(): void {
    if (!this.isStartOverDisabled) {
      this.startOver.emit();
    }
  }
}
